import { useState, useEffect } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type AdminDashboard } from '@/services/api';

const emptyStats: AdminDashboard['stats'] = {
  totalUsers: 0,
  newUsersToday: 0,
  totalSupply: 0,
  pendingSupply: 0,
  totalDemand: 0,
  pendingDemand: 0,
  totalReports: 0,
  pendingReports: 0,
  totalNews: 0,
};

const statCards = [
  { key: 'totalUsers', label: '注册用户', icon: 'ri-user-line', color: 'bg-secondary-500' },
  { key: 'totalSupply', label: '供给挂牌', icon: 'ri-arrow-up-circle-line', color: 'bg-accent-500' },
  { key: 'totalDemand', label: '需求挂牌', icon: 'ri-arrow-down-circle-line', color: 'bg-primary-500' },
  { key: 'pendingReports', label: '待处理举报', icon: 'ri-alert-line', color: 'bg-red-500' },
];

export default function AdminDashboardPage() {
  const [timeRange, setTimeRange] = useState<'week' | 'month'>('week');
  const [data, setData] = useState<AdminDashboard | null>(null);

  useEffect(() => {
    adminApi.dashboard().then(setData).catch(() => {});
  }, []);

  const adminStats = data?.stats ?? emptyStats;
  const recentActivities = data?.recentActivities ?? [];
  const chartData = data?.chartData ?? { weeklyUsers: [], monthlyListings: [] };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Page title */}
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">首页看板</h2>
          <p className="text-sm text-foreground-500 mt-0.5">平台运营数据概览</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {statCards.map((card) => {
            const value = adminStats[card.key as keyof typeof adminStats];
            return (
              <div key={card.key} className="bg-white rounded-xl p-4 border border-background-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-foreground-500">{card.label}</span>
                  <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center`}>
                    <i className={`${card.icon} text-white text-sm`} />
                  </div>
                </div>
                <div className="text-2xl font-bold text-foreground-950">{value.toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* User trend chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-background-200 p-4 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground-950">供需挂牌趋势</h3>
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
                {(['week', 'month'] as const).map((r) => (
                  <button
                    key={r}
                    onClick={() => setTimeRange(r)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      timeRange === r
                        ? 'bg-white text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {r === 'week' ? '近7天' : '近6月'}
                  </button>
                ))}
              </div>
            </div>
            {/* Bar chart */}
            <div className="h-56 flex items-end gap-2 md:gap-4">
              {chartData.monthlyListings.map((item) => (
                <div key={item.month} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="w-full flex flex-col items-center gap-1">
                    {/* Supply bar */}
                    <div className="relative w-full max-w-[32px]">
                      <div
                        className="w-full bg-accent-500 rounded-t-md transition-all duration-500"
                        style={{ height: `${(item.supply / 200) * 140}px` }}
                      />
                    </div>
                    {/* Demand bar */}
                    <div className="relative w-full max-w-[32px]">
                      <div
                        className="w-full bg-primary-500 rounded-t-md transition-all duration-500"
                        style={{ height: `${(item.demand / 200) * 140}px` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-foreground-500">{item.month}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-6 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-accent-500" />
                <span className="text-xs text-foreground-500">供给</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-primary-500" />
                <span className="text-xs text-foreground-500">需求</span>
              </div>
            </div>
          </div>

          {/* Pending items */}
          <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">待处理事项</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary-50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-secondary-600 text-sm" />
                  </div>
                  <span className="text-sm text-foreground-700">供给待审核</span>
                </div>
                <span className="text-base font-semibold text-secondary-600">{adminStats.pendingSupply}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary-50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-file-search-line text-primary-600 text-sm" />
                  </div>
                  <span className="text-sm text-foreground-700">需求待审核</span>
                </div>
                <span className="text-base font-semibold text-primary-600">{adminStats.pendingDemand}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <i className="ri-alert-line text-red-600 text-sm" />
                  </div>
                  <span className="text-sm text-foreground-700">待处理举报</span>
                </div>
                <span className="text-base font-semibold text-red-600">{adminStats.pendingReports}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-accent-50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-user-add-line text-accent-600 text-sm" />
                  </div>
                  <span className="text-sm text-foreground-700">今日新用户</span>
                </div>
                <span className="text-base font-semibold text-accent-600">{adminStats.newUsersToday}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent activities */}
        <div className="bg-white rounded-xl border border-background-200 p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">最近动态</h3>
          <div className="space-y-0">
            {recentActivities.map((act, i) => {
              const typeConfig = {
                user: { icon: 'ri-user-line', bg: 'bg-secondary-100', text: 'text-secondary-600' },
                supply: { icon: 'ri-arrow-up-circle-line', bg: 'bg-accent-100', text: 'text-accent-600' },
                demand: { icon: 'ri-arrow-down-circle-line', bg: 'bg-primary-100', text: 'text-primary-600' },
                report: { icon: 'ri-alert-line', bg: 'bg-red-100', text: 'text-red-600' },
                news: { icon: 'ri-newspaper-line', bg: 'bg-amber-100', text: 'text-amber-600' },
              };
              const config = typeConfig[act.type as keyof typeof typeConfig];
              return (
                <div
                  key={act.id}
                  className={`flex items-center gap-3 py-3 ${
                    i < recentActivities.length - 1 ? 'border-b border-background-100' : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${config.icon} ${config.text} text-sm`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{act.action}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-500 truncate">{act.target}</span>
                    </div>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      {new Date(act.time).toLocaleString('zh-CN', {
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}